import React, { Component, CSSProperties } from 'react';
import { SettingOutlined } from '@ant-design/icons/lib';
import { Flex } from 'app/components/common/Flex';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import {
    MapLayerSettingsPopupContent,
    MapLayerSettingsPopupTitle,
} from 'app/components/dashboard/network/map/MapLayerSettingsPopup';
import {
    MapLayer,
    MapLayerCustomProperties,
    MapLayerType,
    MapLayerUtils,
} from 'app/components/dashboard/network/map/Map';
import { LayerUpdateEvent } from 'app/common/eventbus/Events';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { ClearOutlined } from '@ant-design/icons';

interface LayerSelectionEntryShortcutProps {
    layer: MapLayer;
    mapId: number;
    minWidthEm?: string;
}

export class LayerSelectionEntryShortcut extends Component<LayerSelectionEntryShortcutProps> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    render() {
        let flexStyle: CSSProperties = {
            minWidth: this.props.minWidthEm ?? '7em',
            maxWidth: '20em',
            height: '2em',
            textAlign: 'center',
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: '1em', //space between label and settings button
            cursor: MapLayerUtils.hasStatusChangeEnabled(this.props.layer) ? 'pointer' : 'auto', //if the button on click will do nothing then don't show cursor
            paddingLeft: 0,
            paddingRight: 0,
        };
        if (this.props.layer.active) {
            flexStyle = Object.assign(flexStyle, {
                background: '#101010',
            });
        }
        return (
            <Flex
                direction={'vertical'}
                style={flexStyle}
                className={`ant-btn detailed-subpanel-title ${this.props.layer.active ? 'active' : ''}`}>
                <span className="iconOnSpan" onClick={this.handleClick}>
                    {this.props.layer.label}
                </span>
                {this.renderMapLayerSettings()}
                <ConditionalRender condition={this.isWorkspaceLayer()}>
                    <span
                        className="detailed-subpanel-header-right iconOnSpan"
                        style={{ fontSize: '1em', position: 'absolute', right: '0.5em' }}>
                        <ClearOutlined onClick={this.handleClearWorkspaceClick} />
                    </span>
                </ConditionalRender>
            </Flex>
        );
    }

    renderMapLayerSettings() {
        return (
            <span
                className="detailed-subpanel-header-right iconOnSpan"
                style={{ fontSize: '1em', position: 'absolute', right: this.isWorkspaceLayer() ? `2em` : `0.5em` }}>
                <ClosablePopover
                    key="map-settings-action"
                    ref={this.settingsPopoverRef}
                    trigger="click"
                    placement="bottom"
                    title={<MapLayerSettingsPopupTitle />}
                    content={
                        <MapLayerSettingsPopupContent
                            settings={this.props.layer}
                            onChange={this.handleSettingsChanges}
                            onClose={() => this.settingsPopoverRef.current.hide()}
                        />
                    }>
                    <SettingOutlined onClick={this.handleSettingsClick} />
                </ClosablePopover>
            </span>
        );
    }

    handleClick = () => {
        if (MapLayerUtils.hasStatusChangeEnabled(this.props.layer)) {
            const newLayer: MapLayer = Object.assign({}, this.props.layer, { active: !this.props.layer.active });
            LayerUpdateEvent.fire(this.props.mapId, newLayer);
        }
    };

    handleClearWorkspaceClick = () =>
        LayerUpdateEvent.fire(this.props.mapId, Object.assign({}, this.props.layer, { objectsToDisplay: [] }));

    handleSettingsClick = event => {
        event.stopPropagation();
        this.settingsPopoverRef.current.show();
    };

    handleSettingsChanges = (layer: MapLayer) => {
        this.settingsPopoverRef.current.hide();
        LayerUpdateEvent.fire(this.props.mapId, layer);
    };

    isWorkspaceLayer = () => this.props.layer.type === MapLayerType.WORKSPACE;
}

export const MemoizedLayerSelectionEntryShortcut = React.memo(
    (props: LayerSelectionEntryShortcutProps) => {
        return <LayerSelectionEntryShortcut {...props} />;
    },
    (prevProps, nextProps) =>
        prevProps.mapId === nextProps.mapId &&
        prevProps.layer.id === nextProps.layer.id &&
        prevProps.layer.active === nextProps.layer.active,
);
